/* eslint-disable eslint-comments/disable-enable-pair */

import { Stack } from "@mui/material";
import {
  APP_V2_APP_EVENTS,
  APP_V2_USER_EVENTS,
  logError,
  logEvent,
} from "@src/appV2/lib/analytics";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import { BottomSheet, type UseModalState } from "@src/appV2/redesign/system/ui-components";
import { getSafeAreaPaddings } from "@src/appV2/redesign/system/ui-theme";
import { useCallback, useState } from "react";

import { BottomSheetHeader } from "../../../components/BottomSheetHeader";
import { type Facility } from "../../data/schemas";
import { useSubmitInterest } from "../../hooks/useSubmitInterest";
import { type ContactInfoFormValues, ContactInfoSheet } from "../ContactInfoSheet/ContactInfoSheet";
import { InitialQuestionSheet } from "../InitialQuestionSheet/InitialQuestionSheet";
import { InterestConfirmationSheet } from "../InterestConfirmationSheet/InterestConfirmationSheet";
import { ShiftPreferencesSheet } from "../ShiftPreferencesSheet/ShiftPreferencesSheet";
import { SuccessScreen } from "../SuccessScreen/SuccessScreen";

interface FacilityBottomSheetProps {
  facility: Facility | undefined;
  modalState: UseModalState;
}

type FlowStep = "interest" | "initial" | "contact" | "preferences" | "success";

export function FacilityBottomSheet(props: FacilityBottomSheetProps) {
  const { facility = undefined, modalState } = props;
  const [currentStep, setCurrentStep] = useState<FlowStep>("interest");
  const setCurrentStepWithAnalytics = useCallback(
    (newStep: FlowStep) => {
      setCurrentStep((oldStep) => {
        logEvent(APP_V2_USER_EVENTS.EARLY_ACCESS_FACILITIES_INTEREST_FLOW_STEP_CHANGED, {
          facilityId: facility?.id,
          facilityName: facility?.name,
          newStep,
          oldStep,
        });
        return newStep;
      });
    },
    [facility?.id, facility?.name]
  );
  const [workedHereBefore, setWorkedHereBefore] = useState(false);
  const [contactInfo, setContactInfo] = useState<ContactInfoFormValues | undefined>(undefined);

  const { showErrorToast, showSuccessToast } = useToast();

  const { mutate: submitInterest, isLoading: isPending } = useSubmitInterest({
    onSuccess: () => {
      setCurrentStepWithAnalytics("success");
      showSuccessToast("Your interest has been submitted successfully!");
      logEvent(APP_V2_USER_EVENTS.EARLY_ACCESS_FACILITIES_INTEREST_FORM_SUBMITTED, {
        facilityId: facility?.id,
        facilityName: facility?.name,
      });
    },
    onError: (error: Error) => {
      logError(APP_V2_APP_EVENTS.EARLY_ACCESS_FACILITIES_INTEREST_FORM_ERROR, {
        error,
        metadata: {
          facilityId: facility?.id,
          facilityName: facility?.name,
        },
      });
      showErrorToast(error.message ?? "Failed to submit interest. Please try again.");
    },
  });

  function handleClose() {
    setCurrentStepWithAnalytics("interest");
    setWorkedHereBefore(false);
    setContactInfo(undefined);
    modalState.closeModal();
  }

  if (!facility) {
    return null;
  }

  return (
    <BottomSheet
      modalState={modalState}
      paperSx={(theme) => ({
        ...getSafeAreaPaddings({ bottom: true, fallbackBottom: theme.spacing(4) }),
      })}
      header={
        <BottomSheetHeader
          onClose={() => {
            handleClose();
          }}
        />
      }
    >
      <Stack>
        {currentStep === "interest" && (
          <InterestConfirmationSheet
            facility={facility}
            onYes={() => {
              setCurrentStepWithAnalytics("initial");
            }}
            onNo={() => {
              setCurrentStepWithAnalytics("interest");
              handleClose();
            }}
          />
        )}

        {currentStep === "initial" && (
          <InitialQuestionSheet
            facility={facility}
            onYes={() => {
              setWorkedHereBefore(true);
              setCurrentStepWithAnalytics("contact");
            }}
            onNo={() => {
              setWorkedHereBefore(false);
              setCurrentStepWithAnalytics("preferences");
            }}
          />
        )}

        {currentStep === "contact" && (
          <ContactInfoSheet
            onSubmit={(data) => {
              setContactInfo(data);
              setCurrentStepWithAnalytics("preferences");
            }}
            onSkip={() => {
              setContactInfo(undefined);
              setCurrentStepWithAnalytics("preferences");
            }}
            onBack={() => {
              setCurrentStepWithAnalytics("initial");
            }}
          />
        )}

        {currentStep === "preferences" && (
          <ShiftPreferencesSheet
            facility={facility}
            isSubmitting={isPending}
            onSubmit={(data) => {
              // User knows someone if they provided any contact information
              const knowsSomeone = Boolean(
                contactInfo?.contactName ?? contactInfo?.contactEmail ?? contactInfo?.contactPhone
              );

              const contactName = contactInfo?.contactName ?? "";
              const contactEmail = contactInfo?.contactEmail ?? "";
              const contactPhone = contactInfo?.contactPhone ?? "";
              const comments = contactInfo?.comments ?? "";

              submitInterest({
                facilityId: facility.id,
                facilityName: facility.name,
                workedHereBefore,
                knowsSomeone,
                contactName,
                contactEmail,
                contactPhone,
                comments,
                preferredShiftTypes: data.preferredShiftTypes,
                additionalNotes: data.additionalNotes ?? "",
              });
            }}
            onBack={() => {
              if (workedHereBefore) {
                setCurrentStepWithAnalytics("contact");
              } else {
                setCurrentStepWithAnalytics("initial");
              }
            }}
          />
        )}

        {currentStep === "success" && (
          <SuccessScreen
            facility={facility}
            onClose={() => {
              handleClose();
            }}
          />
        )}
      </Stack>
    </BottomSheet>
  );
}
